import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import BookingCalendar from '../BookingCalendar';
import ApprovalStatusLegend from '../ApprovalStatusLegend';
import CalendarFeedModal from '../modal/CalendarFeedModal';
import UnitType from '../../constants/UnitType';
import UserRole from '../../constants/UserRole';
import ApprovalStatus from '../../constants/ApprovalStatus';

import { UserContext } from '../../contexts/UserContext';

import * as api from '../../apis';

const DashboardCalendar = ({ unitType, standalone }) => {
	const { user } = useContext(UserContext);
	const [unitList, setUnitList] = useState(null);
	const [calendarUnitId, setCalendarUnitId] = useState(null);
	const [approvalStatuses, setApprovalStatuses] = useState([]);
	const [calendarFeedModalOpen, setCalendarFeedModalOpen] = useState(false);

	useEffect(() => {
		let filterParams = [`unit_type=${unitType}`, `page_size=${Number.MAX_SAFE_INTEGER}`];
		// 사용자가 관리자 권한이 없는 경우 Unit 관리 대상만 얻는다.
		if (!UserRole.isAdminRole(user?.user_role)) {
			user.unit_admins.forEach((unit) => filterParams.push(`unit_ids[]=${unit.unit_id}`));
		}

		api.getUnits(filterParams)
			.then((res) => {
				if (res && res.data) {
					setUnitList(res.data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}, []);

	const onOpenStandalone = () => {
		window.open(`/admin/calendar/${unitType}`);
	};

	const onOpenCalendarFeedModal = () => {
		setCalendarFeedModalOpen(true);
	};

	const onClickApprovalCheckbox = (status) => {
		setApprovalStatuses(
			approvalStatuses.includes(status)
				? approvalStatuses.filter((s) => s !== status)
				: [...approvalStatuses, status]
		);
	};

	return (
		<>
			<UnitSelectContainer>
				<UnitSelectTitle>{`${UnitType.toString(unitType)} 선택`}</UnitSelectTitle>
				<UnitSelect value={calendarUnitId ?? ''} onChange={(e) => setCalendarUnitId(e.currentTarget.value)}>
					<option value="">전체</option>
					{unitList?.items.map((unit, idx) => {
						return (
							<option key={idx} value={unit.id}>
								{unit.name ?? '-'}
							</option>
						);
					})}
				</UnitSelect>
				<ButtonGroup>
					{!standalone && (
						<OpenStandaloneButton onClick={onOpenStandalone}>새 창으로 보기</OpenStandaloneButton>
					)}
					<CalendarFeedButton onClick={onOpenCalendarFeedModal}>캘린더 피드</CalendarFeedButton>
				</ButtonGroup>
			</UnitSelectContainer>
			{calendarFeedModalOpen && (
				<CalendarFeedModal
					unitType={unitType}
					unitId={calendarUnitId || null}
					approvalStatuses={approvalStatuses}
					onClose={() => setCalendarFeedModalOpen(false)}
				/>
			)}
			<ApprovalSelectContainer>
				<ApprovalSelectTitle>예약 상태</ApprovalSelectTitle>
				<ApprovalCheckboxContainer>
					<ApprovalCheckbox>
						<ApprovalCheckboxInput
							type="checkbox"
							id="approved"
							checked={approvalStatuses.includes(ApprovalStatus.APPROVED)}
							onChange={() => onClickApprovalCheckbox(ApprovalStatus.APPROVED)}
						/>
						<ApprovalCheckboxLabel htmlFor="approved">승인됨</ApprovalCheckboxLabel>
					</ApprovalCheckbox>
					<ApprovalCheckbox>
						<ApprovalCheckboxInput
							type="checkbox"
							id="pending"
							checked={approvalStatuses.includes(ApprovalStatus.PENDING)}
							onChange={() => onClickApprovalCheckbox(ApprovalStatus.PENDING)}
						/>
						<ApprovalCheckboxLabel htmlFor="pending">승인대기</ApprovalCheckboxLabel>
					</ApprovalCheckbox>
				</ApprovalCheckboxContainer>
			</ApprovalSelectContainer>
			<ApprovalStatusLegend />
			<BookingCalendar
				standalone={standalone}
				unitType={unitType}
				calendarUnitId={calendarUnitId}
				approvalStatuses={approvalStatuses}
			/>
		</>
	);
};

const UnitSelectContainer = styled.div`
	display: flex;
	align-items: center;
	margin: 20px 0;
`;

const UnitSelectTitle = styled.div`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 19px;
	color: #000000;
`;

const UnitSelect = styled.select`
	width: 250px;
	height: 35px;
	padding: 8px;
	margin-left: 14px;

	border: 1px solid #dddddd;
	border-radius: 5px;

	appearance: none;
	background-image: linear-gradient(45deg, transparent 50%, #000000 50%),
		linear-gradient(135deg, #000000 50%, transparent 50%);
	background-position: calc(100% - 20px) calc(1em + 0px), calc(100% - 15px) calc(1em + 0px), calc(100% - 2.5em) 0.5em;
	background-size: 5px 5px, 5px 5px, 1px 1.5em;
	background-repeat: no-repeat;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #333333;
`;

const ApprovalSelectContainer = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 20px;
	gap: 14px;
`;

const ApprovalSelectTitle = styled.div`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 19px;
	color: #000000;
`;

const ApprovalCheckboxContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

const ApprovalCheckbox = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
`;

const ApprovalCheckboxInput = styled.input`
	width: 15px;
	height: 15px;
	border: 1px solid #c5c5c5;
	border-radius: 3px;
	cursor: pointer;
`;

const ApprovalCheckboxLabel = styled.label`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #000000;
	cursor: pointer;
`;

const ButtonGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-left: 10px;
`;

const OpenStandaloneButton = styled.div`
	width: 110px;
	height: 33px;

	background-color: #2b62ab;
	border-radius: 5px;
	cursor: pointer;

	display: flex;
	align-items: center;
	justify-content: center;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 14px;
	line-height: 17px;
	color: #ffffff;

	&:hover {
		background-color: #0c438d;
	}
`;

const CalendarFeedButton = styled(OpenStandaloneButton)`
	width: 120px;
`;

export default DashboardCalendar;
